/**
 * Serviço unificado para upload de áudios
 * Envia áudios ao armazenamento da Hostinger.
 */

import { getAccessToken } from './authApi';

const HOSTINGER_API_URL = 'https://nutrisync.dgapp.com.br/api/upload-audio.php';
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 segundo

export interface AudioUploadResponse {
  success: boolean;
  url?: string;
  error?: string;
  source?: 'hostinger';
}

/**
 * Aguarda um tempo específico (para retry com backoff)
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Testa a conectividade com a API do Hostinger
 */
export async function testHostingerAudioAPI(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

    const response = await fetch(HOSTINGER_API_URL, {
      method: 'OPTIONS',
      headers: {
        'Accept': 'application/json',
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response.ok || response.status === 200;
  } catch (error) {
    console.warn('[AudioUpload] Teste de conectividade com Hostinger falhou:', error);
    return false;
  }
}

/**
 * Faz upload de um áudio para o Hostinger
 */
async function uploadToHostinger(
  file: File,
  userId: string,
  retryCount: number = 0
): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('userId', userId);

    console.log(`[AudioUpload] Hostinger - Tentativa ${retryCount + 1}/${MAX_RETRIES + 1}`);
    console.log(`[AudioUpload] Arquivo: ${file.name}, Tamanho: ${file.size} bytes`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 segundos timeout para áudios

    const accessToken = getAccessToken();
    const response = await fetch(HOSTINGER_API_URL, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const responseText = await response.text();
    const contentType = response.headers.get('content-type') || '';

    console.log(`[AudioUpload] Hostinger - Status: ${response.status}, Content-Type: ${contentType}`);

    if (!response.ok) {
      // Se for erro de servidor (500) e ainda temos retries, tentar novamente
      if (response.status === 500 && retryCount < MAX_RETRIES) {
        const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        console.log(`[AudioUpload] Hostinger - Erro 500, tentando novamente em ${delayMs}ms...`);
        await delay(delayMs);
        return uploadToHostinger(file, userId, retryCount + 1);
      }
      
      throw new Error(`HTTP ${response.status}: ${responseText.substring(0, 200)}`);
    }

    // Verificar se retornou HTML em vez de JSON
    if (responseText.trim().startsWith('<') || !contentType.includes('application/json')) {
      console.error('[AudioUpload] Hostinger retornou HTML em vez de JSON');
      throw new Error('Servidor retornou resposta inválida');
    }

    let result: AudioUploadResponse;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('[AudioUpload] Erro ao parsear JSON:', responseText.substring(0, 200));
      throw new Error('Resposta do servidor inválida');
    }

    if (!result.success || !result.url) {
      throw new Error(result.error || 'Upload falhou sem URL');
    }

    console.log(`[AudioUpload] Hostinger - Upload bem-sucedido: ${result.url}`);
    return result.url;
  } catch (error: any) {
    // Se for erro de rede/timeout e ainda temos retries, tentar novamente
    const isNetworkError = 
      error.name === 'AbortError' ||
      error.message?.includes('Network') || 
      error.message?.includes('timeout') ||
      error.message?.includes('fetch');

    if (isNetworkError && retryCount < MAX_RETRIES) {
      const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      console.log(`[AudioUpload] Hostinger - Erro de rede, tentando novamente em ${delayMs}ms...`);
      await delay(delayMs);
      return uploadToHostinger(file, userId, retryCount + 1);
    }
    
    console.error('[AudioUpload] Hostinger - Erro final:', error);
    throw error;
  }
}

/**
 * Upload de áudio para a Hostinger.
 * 
 * @param file - Arquivo de áudio a ser enviado
 * @param userId - ID do usuário que está fazendo o upload
 * @returns Objeto com a URL do áudio na Hostinger
 */
export async function uploadAudio(
  file: File,
  userId: string
): Promise<AudioUploadResponse> {
  // Validar arquivo
  if (!file) {
    throw new Error('Nenhum arquivo fornecido');
  }

  if (!file.type.startsWith('audio/')) {
    throw new Error('Arquivo deve ser um áudio');
  }

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error('Áudio deve ter no máximo 10MB');
  }

  console.log(`[AudioUpload] Iniciando upload: ${file.name} (${file.size} bytes)`);

  const url = await uploadToHostinger(file, userId);
  return { success: true, url, source: 'hostinger' };
}

/**
 * Converte Blob para File
 */
export function blobToFile(blob: Blob, filename: string, mimeType: string): File {
  return new File([blob], filename, { type: mimeType });
}







